import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  PencilIcon, 
  ArrowDownTrayIcon, 
  ShieldCheckIcon, 
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI, historyAPI, policyAPI, formatCurrency, formatDateTime, getStatusColor, getStatusLabel, getOfferTypeLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Récupérer les souscriptions
  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery(
    'user-subscriptions',
    () => subscriptionAPI.getSubscriptions()
  );

  // Récupérer l'historique (6 derniers mois par défaut)
  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['user-history', user?.id],
    () => historyAPI.getHistory(user.id, { limit: 5 }),
    { enabled: !!user?.id }
  );

  // Récupérer les polices
  const { data: policiesData, isLoading: policiesLoading } = useQuery(
    'user-policies-dashboard',
    () => policyAPI.getUserPolicies()
  );

  // Récupérer les cotisations
  const { data: contributionsData, isLoading: contributionsLoading } = useQuery(
    'user-contributions-dashboard',
    () => policyAPI.getUserContributions({ limit: 10 })
  );

  const subscriptions = subscriptionsData?.data?.subscriptions || [];
  const histories = historyData?.data?.histories || [];
  const policies = policiesData?.data?.policies || [];
  const contributions = contributionsData?.data?.contributions || [];

  // Calculer les statistiques
  const stats = {
    totalPolicies: policies.length,
    activePolicies: policies.filter(p => p.status === 'ACTIVE').length,
    totalContributions: contributions.length,
    paidContributions: contributions.filter(c => c.status === 'PAID').length,
    overdueContributions: contributions.filter(c => {
      if (c.status === 'PAID') return false;
      return new Date(c.dueDate) < new Date();
    }).length,
    totalAmountPaid: contributions
      .filter(c => c.status === 'PAID')
      .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0)
  };

  const handleExportHistory = async () => {
    try {
      const response = await historyAPI.exportHistory(user.id);
      console.log('Export data:', response.data);
      // TODO: Générer et télécharger le PDF
      alert('Export PDF en cours de développement');
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  if (subscriptionsLoading || historyLoading || policiesLoading || contributionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Tableau de bord - Utilisateur
        </h1>
        <p className="text-gray-600">
          Bienvenue {user?.mutualiste ? user.mutualiste.prenoms.split(' ')[0] : 'Utilisateur'}, voici un aperçu de votre compte mutualiste.
        </p>
      </div>

      {/* Statistiques visuelles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Polices actives */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Polices actives</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.activePolicies}</p>
              <p className="text-xs text-gray-400">sur {stats.totalPolicies} total</p>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/policies')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Voir mes polices →
            </button>
          </div>
        </div>

        {/* Cotisations payées */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Cotisations payées</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.paidContributions}</p>
              <p className="text-xs text-gray-400">sur {stats.totalContributions} total</p>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/contributions')}
              className="text-sm text-green-600 hover:text-green-500"
            >
              Voir mes cotisations →
            </button>
          </div>
        </div>

        {/* Cotisations en retard */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className={`h-8 w-8 ${stats.overdueContributions > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">En retard</p>
              <p className={`text-2xl font-semibold ${stats.overdueContributions > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {stats.overdueContributions}
              </p>
              <p className="text-xs text-gray-400">cotisations</p>
            </div>
          </div>
          {stats.overdueContributions > 0 && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/contributions?status=OVERDUE')}
                className="text-sm text-red-600 hover:text-red-500"
              >
                Payer maintenant →
              </button>
            </div>
          )}
        </div>

        {/* Montant total payé */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total payé</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(stats.totalAmountPaid)}</p>
              <p className="text-xs text-gray-400">cotisations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertes et notifications */}
      {(stats.overdueContributions > 0 || policies.some(p => p.status === 'PENDING')) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Attention !</span>
                {stats.overdueContributions > 0 && (
                  <span> Vous avez {stats.overdueContributions} cotisation(s) en retard.</span>
                )}
                {policies.some(p => p.status === 'PENDING') && (
                  <span> Certaines de vos polices sont en attente d'activation.</span>
                )}
              </p>
              <div className="mt-2 flex space-x-3">
                {stats.overdueContributions > 0 && (
                  <button
                    onClick={() => navigate('/contributions?status=OVERDUE')}
                    className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md hover:bg-yellow-200"
                  >
                    Payer les cotisations
                  </button>
                )}
                {policies.some(p => p.status === 'PENDING') && (
                  <button
                    onClick={() => navigate('/policies')}
                    className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md hover:bg-yellow-200"
                  >
                    Voir mes polices
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mes produits souscrits */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-6">
          Mes produits souscrits
        </h2>
        
        {subscriptions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Aucune souscription active</p>
            <button 
              onClick={() => navigate('/offers')}
              className="btn-primary"
            >
              Découvrir nos produits
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscriptions.map((subscription) => (
              <div key={subscription.id} className="card p-6 hover:shadow-card-hover transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      {subscription.offer.type === 'HEALTH_COVERAGE' && (
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">+</span>
                        </div>
                      )}
                      {subscription.offer.type === 'SAVINGS' && (
                        <div className="w-6 h-6 bg-secondary-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">💰</span>
                        </div>
                      )}
                      {subscription.offer.type === 'LOAN' && (
                        <div className="w-6 h-6 bg-accent-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">$</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {getOfferTypeLabel(subscription.offer.type)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {subscription.offer.name}
                      </p>
                    </div>
                  </div>
                  <button className="p-1 text-gray-400 hover:text-gray-600">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                    {getStatusLabel(subscription.status?.libelle || subscription.status)}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(subscription.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historique */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-primary-900">
            Historique
          </h2>
          <button
            onClick={handleExportHistory}
            className="btn-secondary btn-sm flex items-center"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Télécharger
          </button>
        </div>

        {/* Liste des transactions */}
        <div className="space-y-3">
          {histories.map((history) => (
            <div key={history.id} className="py-3 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium text-gray-600">
                      {formatDateTime(history.createdAt)}
                    </span>
                    {' - '}
                    <span>{history.description}</span>
                  </p>
                </div>
                {history.amount && (
                  <div className="text-right ml-4">
                    <p className="font-medium text-gray-900">
                      {formatCurrency(history.amount)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {histories.length === 0 && (
          <p className="text-gray-500 text-center py-8">
            Aucun historique disponible
          </p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
